import React, { useState } from 'react';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

/**
 * Update a challan's payment due date, with a required remark. Shows who last
 * changed it (audit trail is kept server-side).
 * Props: row, onClose(), onSubmit({due_date, remark}) -> {success,error}
 */
export default function EditDueDateModal({ row, onClose, onSubmit }) {
  // The API sends a date-only string today; slicing keeps a datetime from
  // rendering as an empty field while still submitting the untouched value.
  const [dueDate, setDueDate] = useState(String(row?.due_date || '').slice(0, 10));
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!dueDate) { setErr('Pick a due date'); return; }
    if (!remark.trim()) { setErr('Remark is required'); return; }
    setSubmitting(true);
    const res = await onSubmit({ due_date: dueDate, remark: remark.trim() });
    setSubmitting(false);
    if (res && res.success === false) { setErr(res.error || 'Failed to update due date'); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Update Due Date</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          <div className="flex justify-between bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-gray-500">Challan</span>
            <span className="font-semibold">{row?.challan_number}</span>
          </div>
          <div className="flex justify-between px-3">
            <span className="text-gray-500">Current due date</span>
            <span className="font-semibold">{fmt(row?.due_date)}</span>
          </div>
          {row?.due_date_overridden && (
            <p className="text-xs text-gray-500 px-3">
              Last changed by {row?.due_date_updated_by_name || 'someone'}
              {row?.due_date_remark ? ` — "${row.due_date_remark}"` : ''}
            </p>
          )}
          <div>
            <label className="block font-medium text-gray-600 mb-1">New due date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
          </div>
          <div>
            <label className="block font-medium text-gray-600 mb-1">Remark <span className="text-red-500">*</span></label>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
                      placeholder="Reason for changing the due date"
                      className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
          </div>
          {err && <p className="text-red-600 text-xs">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border-2 border-gray-300">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">
              {submitting ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
